import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrderProvider } from "@/contexts/OrderContext";
import { ReviewProvider } from "@/contexts/ReviewContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { RouteScrollManager } from "@/components/RouteScrollManager";

// Lazy-load non-critical UI components to reduce main bundle size and improve FCP
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const OfflineIndicator = lazy(() => import("@/components/ui/advanced-loading").then(m => ({ default: m.OfflineIndicator })));
const ConsentManager = lazy(() => import("@/components/consent/ConsentManager").then(m => ({ default: m.ConsentManager })));

// Import code-split route components
import {
  IndexRoute,
  ReportsRoute,
  DevicesRoute,
  LaunchpadRoute,
  OnboardingRoute,
  AboutRoute,
  SupportRoute,
  CheckoutRoute,
  QuotationSuccessRoute,
  OrdersRoute,
  AccountRoute,
  PaymentSuccessRoute,
  PaymentCanceledRoute,
  AdminLoginRoute,
  AdminDashboardRoute,
  PrivacyPolicyRoute,
  TermsOfServiceRoute,
  CookiePolicyRoute,
  NotFoundRoute
} from "@/lib/route-code-splitting";

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <ReviewProvider>
                <PaywallProvider>
                  <OrderProvider>
                    <CartProvider>
                      <TooltipProvider>
                        <Suspense fallback={null}>
                          <Toaster />
                          <Sonner />
                        </Suspense>
                        <BrowserRouter>
                          <RouteScrollManager />
                          <Routes>
                            <Route path="/" element={<IndexRoute />} />
                            <Route path="/onboarding" element={<OnboardingRoute />} />
                            <Route path="/devices" element={<DevicesRoute />} />
                            <Route path="/reports" element={<ReportsRoute />} />
                            <Route path="/launchpad" element={<LaunchpadRoute />} />
                            <Route path="/about" element={<AboutRoute />} />
                            <Route path="/support" element={<SupportRoute />} />
                            <Route path="/checkout" element={<CheckoutRoute />} />
                            <Route path="/quotation-success" element={<QuotationSuccessRoute />} />
                            <Route path="/orders" element={<OrdersRoute />} />
                            <Route path="/orders/:orderId" element={<OrdersRoute />} />
                            <Route path="/account" element={<AccountRoute />} />
                            <Route path="/payment-success" element={<PaymentSuccessRoute />} />
                            <Route path="/payment-canceled" element={<PaymentCanceledRoute />} />
                            <Route path="/subscription-success" element={<PaymentSuccessRoute />} />
                            <Route path="/subscription-canceled" element={<PaymentCanceledRoute />} />
                            <Route path="/admin" element={<AdminLoginRoute />} />
                            <Route path="/admin/dashboard" element={<AdminDashboardRoute />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
                            <Route path="/terms-of-service" element={<TermsOfServiceRoute />} />
                            <Route path="/cookie-policy" element={<CookiePolicyRoute />} />
                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFoundRoute />} />
                          </Routes>
                          <Suspense fallback={null}>
                            <OfflineIndicator />
                            <ConsentManager />
                            
                          </Suspense>
                        </BrowserRouter>
                      </TooltipProvider>
                    </CartProvider>
                  </OrderProvider>
                </PaywallProvider>
              </ReviewProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
